import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService } from '../../services/api';
import { CameraDetails, Store } from '../../types';
import { ModEligibilityConfirmDialog } from './components/ModEligibilityConfirmDialog';
import { ModEligibilityRow, ModEligibilityTable } from './components/ModEligibilityTable';

interface StorePickerProps {
  stores: Store[];
  current: Store | null;
  onSelect: (store: Store) => void;
}

const StorePicker: React.FC<StorePickerProps> = ({ stores, current, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return stores;
    return stores.filter(
      (s) =>
        s.legibleName.toLowerCase().includes(q) ||
        s.storeName.toLowerCase().includes(q) ||
        s.chainLegibleName.toLowerCase().includes(q)
    );
  }, [stores, query]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm min-w-[240px]"
      >
        <span className="truncate text-left flex-1">
          {current ? current.legibleName : 'Select store'}
        </span>
        <KeyboardArrowDownIcon className="w-4 h-4 text-gray-500" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-20">
          <div className="p-2 border-b">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter..."
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="max-h-80 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
            ) : (
              filtered.map((s) => {
                const isCurrent = current?.storeName === s.storeName;
                return (
                  <button
                    key={s.storeName}
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      setQuery('');
                      if (!isCurrent) onSelect(s);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                      isCurrent ? 'bg-blue-50 text-blue-700' : ''
                    }`}
                  >
                    <div className="truncate font-medium">{s.legibleName}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {s.storeName} · {s.chainLegibleName}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const ModEligibilityPage: React.FC = () => {
  const { storeName } = useParams<{ storeName: string }>();
  const navigate = useNavigate();
  const { token } = useAuthContext();
  const { toggleLoading, showErrorToast, showSuccessToast } = useNotificationContext();

  const [store, setStore] = useState<Store | null>(null);
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [cameras, setCameras] = useState<CameraDetails[]>([]);
  const [camerasLoaded, setCamerasLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [dialogAction, setDialogAction] = useState<'enable' | 'disable' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadData = async () => {
    if (!storeName) return;
    toggleLoading(true);
    setCamerasLoaded(false);
    try {
      const stores = await ApiService.fetchStores(token);
      setAllStores(stores);
      const currentStore = stores.find((s) => s.storeName === storeName);
      if (!currentStore) {
        showErrorToast('Store not found');
        navigate('/stores');
        return;
      }
      setStore(currentStore);
      const fetchedCameras = await ApiService.fetchCameras(token, storeName, { useCache: false });
      setCameras(fetchedCameras);
      setCamerasLoaded(true);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error loading store data');
    } finally {
      toggleLoading(false);
    }
  };

  useEffect(() => {
    // Reset store-scoped state immediately so the previous store's data doesn't flash
    // against the new storeName while the fetch is in flight.
    setStore(null);
    setCameras([]);
    setCamerasLoaded(false);
    setSelectedIds(new Set());
    setSearch('');
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeName, token]);

  const allRows = useMemo<ModEligibilityRow[]>(() => {
    if (!store) return [];
    return cameras.flatMap((camera) =>
      camera.mods.map((mod) => ({
        modCoverageId: mod.modCoverageId,
        tagName: mod.tagName,
        isComplianceEnabled: mod.isComplianceEnabled,
        complianceEligibilitySource: mod.complianceEligibilitySource,
        complianceEligibilityUpdatedByEmail: mod.complianceEligibilityUpdatedByEmail,
        complianceEligibilityUpdatedAt: mod.complianceEligibilityUpdatedAt,
      }))
    );
  }, [cameras, store]);

  const { filteredRows, regexError } = useMemo(() => {
    const trimmed = search.trim();
    if (!trimmed) return { filteredRows: allRows, regexError: null as string | null };

    let regex: RegExp | null = null;
    let error: string | null = null;
    try {
      regex = new RegExp(trimmed, 'i');
    } catch (e) {
      error = e instanceof Error ? e.message : 'Invalid regex';
    }

    const matcher = (value: string | null | undefined): boolean => {
      if (!value) return false;
      return regex ? regex.test(value) : value.toLowerCase().includes(trimmed.toLowerCase());
    };

    const result = allRows.filter((r) => matcher(r.tagName));
    return { filteredRows: result, regexError: error };
  }, [allRows, search]);

  const selectedMods = useMemo(
    () =>
      allRows
        .filter((r) => selectedIds.has(r.modCoverageId))
        .map((r) => ({ modCoverageId: r.modCoverageId, tagName: r.tagName })),
    [allRows, selectedIds]
  );

  const handleConfirm = async () => {
    if (!storeName || !dialogAction || selectedMods.length === 0) return;
    // Block action while data for the current store hasn't finished loading — selection
    // could still reference the previous store's mods.
    if (!camerasLoaded || isSubmitting) return;
    setIsSubmitting(true);
    // Capture the store at invocation time. If the user navigates mid-request the
    // success path won't fire against the wrong store.
    const targetStore = storeName;
    try {
      const payload = { modCoverageIds: selectedMods.map((m) => m.modCoverageId) };
      const result =
        dialogAction === 'enable'
          ? await ApiService.enableModEligibility(token, targetStore, payload)
          : await ApiService.disableModEligibility(token, targetStore, payload);
      if (targetStore !== storeName) return; // user navigated away mid-request
      showSuccessToast(
        `${dialogAction === 'enable' ? 'Enabled' : 'Disabled'} PC & PR for ${result.updatedCount} mod${
          result.updatedCount === 1 ? '' : 's'
        }`
      );
      setDialogAction(null);
      setSelectedIds(new Set());
      await loadData();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error updating mod eligibility');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectionCount = selectedIds.size;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <Link
              to={`/stores/${storeName}`}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
            >
              <ArrowBackIcon />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Manage PC &amp; PR</h1>
              <p className="text-sm text-gray-600">
                {store?.legibleName ?? storeName}
                {store
                  ? ` · ${store.chainLegibleName} (${store.chainName}, ${store.storeName})`
                  : ''}
              </p>
            </div>
          </div>
          <StorePicker
            stores={allStores}
            current={store}
            onSelect={(s) => navigate(`/stores/${s.storeName}/mod-eligibility`)}
          />
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    regexError
                      ? 'border-red-400 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="Search mod (supports regex, e.g. GR-01|GR-05)"
                />
              </div>
              {regexError && (
                <p className="text-xs text-red-600 mt-1">Invalid regex: {regexError}</p>
              )}
            </div>
            <div className="text-sm text-gray-600 whitespace-nowrap">{selectionCount} selected</div>
            <button
              onClick={() => setDialogAction('enable')}
              disabled={selectionCount === 0 || !camerasLoaded || isSubmitting}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Enable PC &amp; PR for selected
            </button>
            <button
              onClick={() => setDialogAction('disable')}
              disabled={selectionCount === 0 || !camerasLoaded || isSubmitting}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Disable PC &amp; PR for selected
            </button>
          </div>
        </div>

        {allRows.length > 0 ? (
          <div className="bg-white rounded-lg shadow-md">
            <ModEligibilityTable
              rows={filteredRows}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              isSearchActive={search.trim().length > 0}
            />
          </div>
        ) : camerasLoaded ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            No cameras found for this store.
          </div>
        ) : null}
      </div>

      <ModEligibilityConfirmDialog
        isOpen={dialogAction !== null}
        action={dialogAction ?? 'enable'}
        mods={selectedMods}
        onConfirm={handleConfirm}
        onClose={() => (isSubmitting ? undefined : setDialogAction(null))}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};
